import { LoaderCircle, Save } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';

import { defaultDailyGoal } from '@calorie-tracker/db';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { trpc } from '@/lib/trpc';

import { fieldClassName } from './constants';
import { formatNumber, getErrorMessage } from './helpers';
import type { AppGoalSummary } from './types';

interface GoalEditorState {
  calories: string;
  carbsGrams: string;
  fatGrams: string;
  proteinGrams: string;
}

interface GoalMacroMath {
  carbCalories: number;
  fatCalories: number;
  remainingCalories: number;
  targetCalories: number;
  proteinCalories: number;
  totalMacroCalories: number;
}

function createGoalEditorState(goal: AppGoalSummary | null): GoalEditorState {
  const values = goal ?? defaultDailyGoal;

  return {
    calories: `${values.calories}`,
    proteinGrams: `${values.proteinGrams}`,
    carbsGrams: `${values.carbsGrams}`,
    fatGrams: `${values.fatGrams}`,
  };
}

function readPositiveNumber(value: string): number | null {
  const numericValue = Number(value);

  return Number.isFinite(numericValue) && numericValue >= 0 ? numericValue : null;
}

function calculateGoalMacroMath(editorState: GoalEditorState): GoalMacroMath | null {
  const targetCalories = readPositiveNumber(editorState.calories);
  const proteinGrams = readPositiveNumber(editorState.proteinGrams);
  const carbsGrams = readPositiveNumber(editorState.carbsGrams);
  const fatGrams = readPositiveNumber(editorState.fatGrams);

  if (
    targetCalories === null ||
    proteinGrams === null ||
    carbsGrams === null ||
    fatGrams === null
  ) {
    return null;
  }

  const proteinCalories = proteinGrams * 4;
  const carbCalories = carbsGrams * 4;
  const fatCalories = fatGrams * 9;
  const totalMacroCalories = proteinCalories + carbCalories + fatCalories;

  return {
    targetCalories,
    proteinCalories,
    carbCalories,
    fatCalories,
    totalMacroCalories,
    remainingCalories: targetCalories - totalMacroCalories,
  };
}

export function GoalsScreen({
  goal,
  todayDate,
}: {
  goal: AppGoalSummary | null;
  todayDate: string;
}) {
  const utils = trpc.useUtils();
  const updateGoal = trpc.goals.updateDaily.useMutation();
  const [editorState, setEditorState] = useState<GoalEditorState>(() =>
    createGoalEditorState(goal),
  );
  const [goalError, setGoalError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const macroMath = calculateGoalMacroMath(editorState);
  const macroMathStatus =
    macroMath === null
      ? 'Enter numbers to check the calorie math.'
      : macroMath.remainingCalories >= 0
        ? `${formatNumber(macroMath.remainingCalories)} calories left for alcohol, fiber, rounding, or unlabeled variance.`
        : `${formatNumber(Math.abs(macroMath.remainingCalories))} calories over your calorie target.`;
  const macroMathStatusClassName =
    macroMath !== null && macroMath.remainingCalories < 0
      ? 'border-amber-200 bg-amber-50 text-amber-900'
      : 'border-border bg-background text-muted-foreground';

  useEffect(() => {
    if (!updateGoal.isPending) {
      setEditorState(createGoalEditorState(goal));
    }
  }, [goal, updateGoal.isPending]);

  function updateField<Key extends keyof GoalEditorState>(key: Key, value: GoalEditorState[Key]) {
    setEditorState((currentState) => ({
      ...currentState,
      [key]: value,
    }));
    setGoalError(null);
    setSavedMessage(null);
  }

  async function submitGoal(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const calories = Number(editorState.calories);
    const proteinGrams = Number(editorState.proteinGrams);
    const carbsGrams = Number(editorState.carbsGrams);
    const fatGrams = Number(editorState.fatGrams);

    if (
      !Number.isFinite(calories) ||
      !Number.isFinite(proteinGrams) ||
      !Number.isFinite(carbsGrams) ||
      !Number.isFinite(fatGrams) ||
      calories <= 0 ||
      proteinGrams < 0 ||
      carbsGrams < 0 ||
      fatGrams < 0
    ) {
      setGoalError('Enter valid goal numbers before saving.');
      setSavedMessage(null);
      return;
    }

    setGoalError(null);
    setSavedMessage(null);

    try {
      const result = await updateGoal.mutateAsync({
        calories,
        proteinGrams,
        carbsGrams,
        fatGrams,
        effectiveFrom: todayDate,
      });

      await utils.today.summary.invalidate({ date: todayDate });
      setSavedMessage(`Goals saved from ${result.goal.effectiveFrom}.`);
    } catch (error) {
      setGoalError(getErrorMessage(error));
    }
  }

  return (
    <div className="space-y-4">
      <Card className="space-y-4">
        <div>
          <p className="text-xs text-muted-foreground">Goals</p>
          <h2 className="mt-1 text-2xl font-semibold">Daily targets</h2>
          <p className="mt-2 text-sm text-muted-foreground">Changes apply from today forward.</p>
        </div>

        <form className="space-y-4" onSubmit={submitGoal}>
          <label className="grid gap-2 text-sm">
            <span className="font-medium">Calories</span>
            <input
              className={fieldClassName}
              inputMode="numeric"
              min="1"
              onChange={(event) => updateField('calories', event.target.value)}
              required
              step="1"
              type="number"
              value={editorState.calories}
            />
          </label>

          <div className="grid gap-3">
            <label className="grid gap-2 text-sm">
              <span className="font-medium">Protein</span>
              <input
                className={fieldClassName}
                inputMode="decimal"
                min="0"
                onChange={(event) => updateField('proteinGrams', event.target.value)}
                required
                step="0.1"
                type="number"
                value={editorState.proteinGrams}
              />
            </label>
            <label className="grid gap-2 text-sm">
              <span className="font-medium">Carbs</span>
              <input
                className={fieldClassName}
                inputMode="decimal"
                min="0"
                onChange={(event) => updateField('carbsGrams', event.target.value)}
                required
                step="0.1"
                type="number"
                value={editorState.carbsGrams}
              />
            </label>
            <label className="grid gap-2 text-sm">
              <span className="font-medium">Fat</span>
              <input
                className={fieldClassName}
                inputMode="decimal"
                min="0"
                onChange={(event) => updateField('fatGrams', event.target.value)}
                required
                step="0.1"
                type="number"
                value={editorState.fatGrams}
              />
            </label>
          </div>

          <div
            className={`space-y-3 rounded-md border px-3 py-3 text-sm ${macroMathStatusClassName}`}
          >
            <div className="flex items-center justify-between gap-3">
              <span>Macro calories</span>
              <span className="font-semibold text-foreground">
                {macroMath
                  ? `${formatNumber(macroMath.totalMacroCalories)} / ${formatNumber(
                      macroMath.targetCalories,
                    )} cal`
                  : 'Waiting'}
              </span>
            </div>
            {macroMath ? (
              <div className="grid grid-cols-3 gap-2 text-xs">
                <span>{formatNumber(macroMath.proteinCalories)} from protein</span>
                <span>{formatNumber(macroMath.carbCalories)} from carbs</span>
                <span>{formatNumber(macroMath.fatCalories)} from fat</span>
              </div>
            ) : null}
            <p className="text-xs leading-5">{macroMathStatus}</p>
          </div>

          {goalError ? (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {goalError}
            </p>
          ) : null}

          {savedMessage ? (
            <p className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
              {savedMessage}
            </p>
          ) : null}

          <Button disabled={updateGoal.isPending} type="submit">
            {updateGoal.isPending ? (
              <>
                <LoaderCircle className="h-4 w-4 animate-spin" />
                Saving
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save goals
              </>
            )}
          </Button>
        </form>
      </Card>

      <Card className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-muted-foreground">Current calories</span>
          <span className="text-sm font-semibold">
            {goal ? `${formatNumber(goal.calories)} cal` : 'Not saved'}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-muted-foreground">Current macros</span>
          <span className="text-sm font-semibold">
            {goal
              ? `${formatNumber(goal.proteinGrams)}p / ${formatNumber(
                  goal.carbsGrams,
                )}c / ${formatNumber(goal.fatGrams)}f`
              : 'Not saved'}
          </span>
        </div>
      </Card>
    </div>
  );
}
